import {
  dbStorage,
  DBSchema,
  PRIMARY_DB_STORE_KEY_PATHS,
  type IndexedDBRecordDeletes,
  type IndexedDBRecordWrites,
} from './indexedDBStorage'
import { indexedDbKeyFromRecord } from './ledgerSafePostgresPrimaryWriteBridge'

/**
 * Cascade deletion over the operational data layer.
 *
 * A held work (Book), held chapter, or witness owns rows scattered across
 * ~20 IndexedDB stores under three field conventions: snake_case
 * (chapter_id / witness_id / book_id), camelCase (chapterId / witnessId),
 * and the ledger-safe layer's contextId — which is always a chapter id.
 * The match rules below are explicit per store so a renamed or missed
 * foreign key is a visible gap in this table, not a silent orphan.
 *
 * The authority layer (manuscript_records, authority_works) is deliberately
 * absent: cascade deletion never touches authority records. Dangling
 * AuthoritySection.primary_chapter_id pointers are the caller's concern
 * (see WorkbenchView), since this module must not depend on stores.
 */

export interface CascadeTarget {
  kind: 'work' | 'chapter' | 'witness'
  id: string
}

export interface CascadeScope {
  bookIds: Set<string>
  chapterIds: Set<string>
  witnessIds: Set<string>
  sourceObjectIds: Set<string>
}

export interface CascadeCounts {
  books: number
  chapters: number
  witnesses: number
  sourceObjects: number
  collationSessions: number
  canonicalTables: number
  derivedRows: number
  total: number
}

export interface CascadePlan {
  scope: CascadeScope
  counts: CascadeCounts
  perStore: Record<string, number>
}

interface ScopeInputChapter {
  id: string
  book_id: string
}

interface ScopeInputWitness {
  id: string
  chapter_id: string
}

interface ScopeInputSourceObject {
  id: string
  work_id: string
}

export interface CascadeScopeInputs {
  chapters: ScopeInputChapter[]
  witnesses: ScopeInputWitness[]
  sourceObjects: ScopeInputSourceObject[]
}

export const buildCascadeScope = (
  target: CascadeTarget,
  inputs: CascadeScopeInputs
): CascadeScope => {
  const scope: CascadeScope = {
    bookIds: new Set(),
    chapterIds: new Set(),
    witnessIds: new Set(),
    sourceObjectIds: new Set(),
  }
  if (target.kind === 'work') {
    scope.bookIds.add(target.id)
    for (const chapter of inputs.chapters) {
      if (chapter.book_id === target.id) {
        scope.chapterIds.add(chapter.id)
      }
    }
    // Source objects are scoped to the held work; they die with it. The
    // ManuscriptRecords they point at are codex-level and survive.
    for (const sourceObject of inputs.sourceObjects) {
      if (sourceObject.work_id === target.id) {
        scope.sourceObjectIds.add(sourceObject.id)
      }
    }
  } else if (target.kind === 'chapter') {
    scope.chapterIds.add(target.id)
  } else {
    scope.witnessIds.add(target.id)
  }
  for (const witness of inputs.witnesses) {
    if (scope.chapterIds.has(witness.chapter_id)) {
      scope.witnessIds.add(witness.id)
    }
  }
  return scope
}

type RecordMatcher = (record: any, scope: CascadeScope) => boolean

const inSet = (set: Set<string>, value: unknown): boolean =>
  typeof value === 'string' && set.has(value)

const operational: RecordMatcher = (record, scope) =>
  inSet(scope.witnessIds, record.witness_id) ||
  inSet(scope.chapterIds, record.chapter_id) ||
  inSet(scope.bookIds, record.book_id)

const chapterScoped: RecordMatcher = (record, scope) =>
  inSet(scope.chapterIds, record.chapter_id) ||
  inSet(scope.bookIds, record.book_id)

/** contextId in the ledger-safe layer is always a chapter id. */
const ledgerContext: RecordMatcher = (record, scope) =>
  inSet(scope.chapterIds, record.contextId)

const STORE_MATCHERS: Partial<Record<keyof DBSchema, RecordMatcher>> = {
  books: (record, scope) => inSet(scope.bookIds, record.id),
  chapters: (record, scope) => inSet(scope.chapterIds, record.id) || inSet(scope.bookIds, record.book_id),
  witnesses: (record, scope) => inSet(scope.witnessIds, record.id) || operational(record, scope),
  witness_text: (record, scope) => inSet(scope.witnessIds, record.witness_id),
  witness_segmentation: (record, scope) => inSet(scope.witnessIds, record.witness_id),
  source_objects: (record, scope) => inSet(scope.sourceObjectIds, record.id),
  source_documents: (record, scope) =>
    inSet(scope.sourceObjectIds, record.source_object_id) || inSet(scope.bookIds, record.work_id),
  section_occurrences: (record, scope) =>
    inSet(scope.sourceObjectIds, record.source_object_id) ||
    inSet(scope.witnessIds, record.witness_id),
  source_coordinate_import_reviews: (record, scope) => {
    const documents = Array.isArray(record.proposed_documents) ? record.proposed_documents : []
    const occurrences = Array.isArray(record.proposed_occurrences) ? record.proposed_occurrences : []
    return documents.some((document: any) =>
      inSet(scope.sourceObjectIds, document.source_object_id) || inSet(scope.bookIds, document.work_id)
    ) || occurrences.some((occurrence: any) =>
      inSet(scope.sourceObjectIds, occurrence.source_object_id) || inSet(scope.witnessIds, occurrence.witness_id)
    )
  },
  alignments: chapterScoped,
  segmentation_baselines: operational,
  edit_operations: operational,
  processing_checkpoints: operational,
  witness_chunk_results: (record, scope) => inSet(scope.witnessIds, record.witness_id),
  witness_comparison_layers: operational,
  witness_source_maps: operational,
  workbench_witness_assignments: (record, scope) =>
    inSet(scope.witnessIds, record.witness_id) || inSet(scope.bookIds, record.source_book_id),
  chapter_scenes: chapterScoped,
  witness_scenes: operational,
  chapter_device_layers: chapterScoped,
  witness_device_layers: operational,
  device_annotation_runs: chapterScoped,
  chapter_character_layers: chapterScoped,
  reader_text_structure_layers: (record, scope) => inSet(scope.chapterIds, record.chapterId),
  chapter_structural_layers: (record, scope) =>
    inSet(scope.chapterIds, record.chapterId) || inSet(scope.bookIds, record.bookId),
  annotation_research_notes: (record, scope) =>
    inSet(scope.chapterIds, record.chapterId) || inSet(scope.bookIds, record.bookId),
  analytical_task_sets: (record, scope) =>
    (Array.isArray(record.workIds) && record.workIds.some((id: string) => inSet(scope.bookIds, id))) ||
    (Array.isArray(record.chapterIds) && record.chapterIds.some((id: string) => inSet(scope.chapterIds, id))) ||
    (Array.isArray(record.witnessIds) && record.witnessIds.some((id: string) => inSet(scope.witnessIds, id))) ||
    (Array.isArray(record.enrollments) && record.enrollments.some((enrollment: any) => (
      inSet(scope.bookIds, enrollment.operationalWorkId) ||
      inSet(scope.chapterIds, enrollment.operationalChapterId) ||
      inSet(scope.witnessIds, enrollment.witnessId)
    ))),
  canonical_alignments: chapterScoped,
  anchored_rows: (record, scope) => inSet(scope.chapterIds, record.chapterId),
  ledger_safe_edit_events: (record, scope) => inSet(scope.chapterIds, record.chapterId),
  ledger_safe_snapshots: (record, scope) => inSet(scope.chapterIds, record.chapterId),
  ledger_safe_ledgers: ledgerContext,
  ledger_safe_ledger_states: ledgerContext,
  ledger_safe_active_ledgers: ledgerContext,
  // scopeId is a chapter id when chapter-scoped; 'global' links never match.
  cross_text_tradition_links: (record, scope) => inSet(scope.chapterIds, record.scopeId),
  translation_preview_resumes: (record, scope) =>
    inSet(scope.chapterIds, record.contextId) || inSet(scope.witnessIds, record.witnessId),
  camera_lucida_inquiries: (record, scope) =>
    inSet(scope.chapterIds, record.chapterId) || inSet(scope.bookIds, record.bookId),
  ledger_safe_agent_change_sets: (record, scope) =>
    inSet(scope.chapterIds, record.chapterId) || inSet(scope.bookIds, record.bookId),
  ledger_safe_authority_heads: (record, scope) =>
    inSet(scope.chapterIds, record.chapterId) || inSet(scope.bookIds, record.bookId),
}

export const CASCADE_DELETE_STORE_NAMES = Object.freeze(Object.keys(STORE_MATCHERS) as Array<keyof DBSchema>)

export const recordMatchesCascadeScope = <T extends keyof DBSchema>(
  store: T,
  record: DBSchema[T],
  scope: CascadeScope
): boolean => {
  const matcher = STORE_MATCHERS[store]
  return matcher ? matcher(record, scope) : false
}

const buildCounts = (perStore: Record<string, number>): CascadeCounts => {
  const summarized = new Set([
    'books', 'chapters', 'witnesses', 'source_objects', 'alignments', 'canonical_alignments',
  ])
  let derivedRows = 0
  let total = 0
  for (const [store, count] of Object.entries(perStore)) {
    total += count
    if (!summarized.has(store)) {
      derivedRows += count
    }
  }
  return {
    books: perStore.books || 0,
    chapters: perStore.chapters || 0,
    witnesses: perStore.witnesses || 0,
    sourceObjects: perStore.source_objects || 0,
    collationSessions: perStore.alignments || 0,
    canonicalTables: perStore.canonical_alignments || 0,
    derivedRows,
    total,
  }
}

const scopeIsEmpty = (scope: CascadeScope): boolean =>
  scope.bookIds.size === 0 && scope.chapterIds.size === 0 &&
  scope.witnessIds.size === 0 && scope.sourceObjectIds.size === 0

export const previewCascade = async (scope: CascadeScope): Promise<CascadePlan> => {
  const perStore: Record<string, number> = {}
  if (scopeIsEmpty(scope)) {
    return { scope, counts: buildCounts(perStore), perStore }
  }
  for (const [store, matcher] of Object.entries(STORE_MATCHERS)) {
    const records = await dbStorage.getAll(store as keyof DBSchema)
    const count = records.filter(record => matcher(record, scope)).length
    if (count > 0) {
      perStore[store] = count
    }
  }
  return { scope, counts: buildCounts(perStore), perStore }
}

export const executeCascade = async (scope: CascadeScope): Promise<CascadePlan> => {
  const perStore: Record<string, number> = {}
  if (scopeIsEmpty(scope)) {
    return { scope, counts: buildCounts(perStore), perStore }
  }
  const deletes: IndexedDBRecordDeletes = {}
  for (const [store, matcher] of Object.entries(STORE_MATCHERS)) {
    const storeName = store as keyof DBSchema
    const records = await dbStorage.getAll(storeName)
    const matchedRecords = records.filter(record => matcher(record, scope))
    if (matchedRecords.length > 0) {
      perStore[store] = matchedRecords.length
      deletes[storeName] = matchedRecords.map(record =>
        indexedDbKeyFromRecord(record, PRIMARY_DB_STORE_KEY_PATHS[storeName])
      )
    }
  }
  // Workbench projects reference books by id list; drop dead references
  // instead of deleting the project.
  const puts: IndexedDBRecordWrites = {}
  if (scope.bookIds.size > 0) {
    const projects = await dbStorage.getAll('workbench_projects')
    const changedProjects = []
    for (const project of projects) {
      const kept = project.source_book_ids.filter(bookId => !scope.bookIds.has(bookId))
      if (kept.length !== project.source_book_ids.length) {
        changedProjects.push({
          ...project,
          source_book_ids: kept,
          last_modified: Date.now(),
        })
      }
    }
    if (changedProjects.length > 0) {
      puts.workbench_projects = changedProjects
    }
  }
  await dbStorage.commitRecordChanges({ puts, deletes })
  return { scope, counts: buildCounts(perStore), perStore }
}
